import { selectors } from 'modules/common';
import { update } from 'modules/immutable';
import { useSelector } from 'modules/react-redux';
import { useCallback } from 'react';

import { Filter, getFilterValue } from '../../../../data/filters';
import { getExpandedColumnsInScope, isCustomField } from '../../../../selectors/reportConfigSelectors';
import { ReportConfigColumn } from '../../../../types/ReportConfigColumn';
import { SectionFiltersProps } from './SectionFilters.types';

export const useSectionFilters = (params: {
    company: selectors.types.ExpandedCompany;
    reportConfig: SectionFiltersProps['reportConfig'];
    onReportConfigChange: SectionFiltersProps['onReportConfigChange'];
    newReport: SectionFiltersProps['newReport'];
}) => {
    const { reportConfig, company, newReport, onReportConfigChange } = params;

    const integration = useSelector((state) => selectors.integration.getIntegrationByCompanyId(state, company.id));
    const integrationName = integration
        ? selectors.integration.getIntegrationTypeName(integration.integrationType)
        : '';

    const hasActiveIntegration = useSelector((state) => selectors.integration.hasActiveIntegration(state, company.id));
    const hasConnectedAirwallex = company.airwallexIntegration?.isConnected;
    const expandedColumns = useSelector((state) => getExpandedColumnsInScope(state, reportConfig));
    const companyTeam = useSelector((state) => selectors.company.getCompanyTeam(state, company));

    const getDefaultVisibleOfColumn = useCallback(
        (configColumn: ReportConfigColumn, filter: Filter) => {
            let visible = configColumn.visible;

            if (
                newReport &&
                configColumn.exactValuesToSetVisibleByDefault &&
                configColumn.exactValuesToSetVisibleByDefault.length
            ) {
                const filterValue = getFilterValue(filter);

                if (filterValue && configColumn.exactValuesToSetVisibleByDefault.includes(filterValue)) {
                    visible = true;
                }
            }

            return visible;
        },
        [newReport]
    );

    const handleChangeFilter = useCallback(
        (column: ReportConfigColumn, filter: Filter) => {
            const key: keyof ReportConfigColumn = isCustomField(column.kind) ? 'id' : 'kind';

            const config = update(reportConfig, 'columns', (configColumns) => {
                return configColumns.map((configColumn) => {
                    if (configColumn[key] === column[key]) {
                        const visible = getDefaultVisibleOfColumn(configColumn, filter);

                        return { ...configColumn, filter, visible };
                    }

                    return configColumn;
                });
            });

            onReportConfigChange(config);
        },
        [getDefaultVisibleOfColumn, onReportConfigChange, reportConfig]
    );

    return {
        integration,
        integrationName,
        hasActiveIntegration,
        hasConnectedAirwallex,
        expandedColumns,
        companyTeam,
        handleChangeFilter,
    };
};
